/**
 * useKpiReferenceLabel — resolves a KpiValueResponse's reference into the
 * right localized "vs ..." string, preferring the semantic `reference_mode`
 * resolved server-side over the raw `reference_offset`.
 *
 * The mechanics of comparison (which window we shifted by) are unchanged;
 * this is purely the *label* the user reads. Centralised here so PulseTile,
 * MyKpisSection, KpiValueDisplay, and the new KpiCard all stay in sync.
 */
import { useI18n } from 'vue-i18n';

const REFERENCE_MODE_KEYS = {
  previous_interval: 'dataset.kpi_card.reference.previous_interval',
  same_interval_previous_day: 'dataset.kpi_card.reference.same_interval_previous_day',
  previous_block: 'dataset.kpi_card.reference.previous_block',
  same_block_previous_day: 'dataset.kpi_card.reference.same_block_previous_day',
  previous_close: 'dataset.kpi_card.reference.previous_close',
  previous_day: 'dataset.kpi_card.reference.previous_day',
  previous_week: 'dataset.kpi_card.reference.previous_week',
  previous_month: 'dataset.kpi_card.reference.previous_month',
  previous_year: 'dataset.kpi_card.reference.previous_year',
};

const REFERENCE_OFFSET_KEYS = {
  '-1d': 'dataset.kpi_card.reference.yesterday',
  '-7d': 'dataset.kpi_card.reference.last_week',
  '-1w': 'dataset.kpi_card.reference.last_week',
  '-30d': 'dataset.kpi_card.reference.last_month',
  '-1M': 'dataset.kpi_card.reference.last_month',
  '-3M': 'dataset.kpi_card.reference.last_3_months',
  '-1y': 'dataset.kpi_card.reference.last_year',
  '-1Y': 'dataset.kpi_card.reference.last_year',
};

export function useKpiReferenceLabel() {
  const { t } = useI18n();

  function referenceLabel(kpiValue) {
    if (!kpiValue) return '';
    const mode = kpiValue.reference_mode;
    if (mode && REFERENCE_MODE_KEYS[mode]) {
      return t(REFERENCE_MODE_KEYS[mode]);
    }
    const offset = kpiValue.reference_offset;
    if (!offset) return '';
    const key = REFERENCE_OFFSET_KEYS[offset];
    return key ? t(key) : `vs ${offset}`;
  }

  return { referenceLabel };
}
